/* eslint-disable camelcase */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
} from '../../../DesignSystems/Kanban';
import Kanban from '../../../DesignSystems/Kanban';
import Loading from '../../../Loading/Loading';
import ResourceCard from './ResourceCard';
import ResourceFilters from './ResourceFilters';
import CheckInDrawer from '../Drawers/CheckInDrawer';
import StatusUpdateDrawer from '../Drawers/StatusUpdateDrawer';
import CheckOutDrawer from '../Drawers/CheckOutDrawer';
import { useWorkAssignments } from '../../hooks/workAssignmentHooks';
import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import { useResourceSets } from '../../hooks/resourceSetHooks';
import { Form } from 'react-bootstrap';
import StylishNewSelect from '../../../DesignSystems/New/StylishNewSelect';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import {
  CHECKED_IN_STATUS_OPTIONS,
  CHECKED_OUT_STATUS_OPTIONS,
  COLUMN_IDS,
  RESOURCE_STATUSES,
  ResourceStatus,
} from '../constants/resourceConstants';
import { createResourcesWithSearchIndex } from '../utils/resourceSearchUtils';
import {
  filterCheckedInResources,
  filterCheckedOutResources,
  filterCheckedOutResourcesByStatus,
  filterResourcesByStatus,
} from '../utils/resourceFilterUtils';
import { createResourceCard } from '../utils/resourceCardUtils';
import { useCurrentUserApprovalAuthority } from 'hooks/incident/useCurrentUserApprovalAuthority';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { toast } from 'react-toastify';

// Type for resource type filter (includes 'all' for no filter)
type ResourceTypeFilter = string | 'all';

// Type for status filters (includes 'all' for no filter)
type StatusFilter = ResourceStatus | 'all';

interface ResourceKanbanProps {
  resources?: ResourceInIncident[];
  isLoading?: boolean;
  onResourceUpdate?: (resource: ResourceInIncident) => void;
  onResourceCancel?: (resource: ResourceInIncident) => void;
  onExportCheckedIn?: () => void;
}

const ResourceKanban: React.FC<ResourceKanbanProps> = ({
  resources = [],
  isLoading = false,
  onResourceUpdate,
  onResourceCancel,
  onExportCheckedIn,
}) => {
  const { exportAsForm } = useIcsFormPdfPreview();
  const { data: workAssignments = [] } = useWorkAssignments();
  const { data: resourceSites = [] } = useResourceSites();
  const { data: resourceSets = [] } = useResourceSets();
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();
  const [selectedResourceType, setSelectedResourceType] =
    useState<ResourceTypeFilter>('all');
  const [selectedCheckedInStatus, setSelectedCheckedInStatus] =
    useState<StatusFilter>('all');
  const [selectedCheckedOutStatus, setSelectedCheckedOutStatus] =
    useState<StatusFilter>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const [showCheckInDrawer, setShowCheckInDrawer] = useState(false);
  const [showUpdateCheckInDrawer, setShowUpdateCheckInDrawer] = useState(false);
  const [showStatusDrawer, setShowStatusDrawer] = useState(false);
  const [showCheckOutDrawer, setShowCheckOutDrawer] = useState(false);
  const [showUpdateCheckOutDrawer, setShowUpdateCheckOutDrawer] =
    useState(false);
  const [selectedResource, setSelectedResource] =
    useState<ResourceInIncident | null>(null);

  // Debounce search text for performance
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  // Create searchable index for each resource
  const resourcesWithSearchIndex = useMemo(() => {
    return createResourcesWithSearchIndex(
      resources,
      resourceSets,
      workAssignments,
      resourceSites
    );
  }, [resources, workAssignments, resourceSites, resourceSets]);

  // Filter resources by type and search text
  const filteredResources = useMemo(() => {
    let filtered = resourcesWithSearchIndex;

    // Filter by resource type
    if (selectedResourceType && selectedResourceType !== 'all') {
      filtered = filtered.filter(
        (r) => r.resource_type === selectedResourceType
      );
    }

    // Filter by search text
    if (debouncedSearchText.trim()) {
      const searchTerms = debouncedSearchText.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter((resource) =>
        searchTerms.every((term) => resource._searchableText.includes(term))
      );
    }

    return filtered;
  }, [resourcesWithSearchIndex, selectedResourceType, debouncedSearchText]);

  // Get unique resource types (which now contains kinds)
  const availableResourceTypes = useMemo(() => {
    const types = new Set<string>();
    resourcesWithSearchIndex.forEach((r) => {
      if (r.resource_type && r.resource_type !== 'Unknown Kind') {
        types.add(r.resource_type);
      }
    });
    return Array.from(types).sort();
  }, [resourcesWithSearchIndex]);

  // Handle card actions
  const handleCheckIn = useCallback((resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowCheckInDrawer(true);
  }, []);

  const handleUpdateCheckIn = useCallback((resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowUpdateCheckInDrawer(true);
  }, []);

  const handleStatusUpdate = useCallback((resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowStatusDrawer(true);
  }, []);

  const handleCheckOut = useCallback((resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowCheckOutDrawer(true);
  }, []);

  const handleUpdateCheckOut = useCallback((resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowUpdateCheckOutDrawer(true);
  }, []);

  // Handler functions for form exports
  const handleExportICS221 = (resource: ResourceInIncident) => {
    void exportAsForm({
      formname: 'ICS_221_CG',
      data: { resourceId: resource.resource_in_incident_id },
      mapSketch: null,
    });
  };

  const handleExportICS238 = () => {
    void exportAsForm({
      formname: 'ICS_238_CG',
      data: {},
      mapSketch: null,
    });
  };

  // Handle card move (drag and drop)
  const handleCardMove = useCallback(
    async (
      card: KanbanCard,
      source: KanbanColumn,
      destination: KanbanColumn
    ) => {
      const resource = card.metadata as ResourceInIncident;
      if (!resource) return;

      // Prevent moving checked-in or checked-out resources back to in transit
      if (
        (source.id === COLUMN_IDS.CHECKED_IN ||
          source.id === COLUMN_IDS.CHECKED_OUT) &&
        destination.id === COLUMN_IDS.IN_TRANSIT
      ) {
        // Not allowed - checked in/out resources cannot go back to in transit
        return;
      }

      // Handle special transitions that require forms
      if (
        source.id === COLUMN_IDS.IN_TRANSIT &&
        destination.id === COLUMN_IDS.CHECKED_IN
      ) {
        // Check if user has approval authority for check-in
        if (!hasApprovalAuthority) {
          toast.error(
            "You don't have permission to check in resources. Please seek permission from a user with approval authority in the incident roster.",
            {
              autoClose: 5000,
            }
          );
          return;
        }
        // Moving from In Transit to Checked In requires check-in form
        handleCheckIn(resource);
        return;
      }

      if (
        source.id === COLUMN_IDS.CHECKED_IN &&
        destination.id === COLUMN_IDS.CHECKED_OUT
      ) {
        // Moving from Checked In to Checked Out requires checkout form
        handleCheckOut(resource);
        return;
      }

      // For other moves, update status directly
      let newStatus = resource.current_status;

      if (destination.id === COLUMN_IDS.IN_TRANSIT) {
        newStatus = RESOURCE_STATUSES.IN_TRANSIT;
      } else if (destination.id === COLUMN_IDS.CHECKED_IN) {
        newStatus = RESOURCE_STATUSES.AVAILABLE;
      }

      // Update resource status
      if (onResourceUpdate && newStatus !== resource.current_status) {
        onResourceUpdate({
          ...resource,
          current_status: newStatus,
          status_since: new Date().toISOString(),
        });
      }
    },
    [onResourceUpdate, handleCheckIn, handleCheckOut, hasApprovalAuthority]
  );

  // Create kanban board data
  const board: KanbanBoard = useMemo(() => {
    const inTransitResources = filteredResources.filter(
      (r) => r.current_status === RESOURCE_STATUSES.IN_TRANSIT
    );

    // Use utility functions for filtering
    let checkedInResources = filterCheckedInResources(filteredResources);
    checkedInResources = filterResourcesByStatus(
      checkedInResources,
      selectedCheckedInStatus
    );

    let checkedOutResources = filterCheckedOutResources(filteredResources);
    checkedOutResources = filterCheckedOutResourcesByStatus(
      checkedOutResources,
      selectedCheckedOutStatus
    );

    const columns: KanbanColumn[] = [
      {
        id: COLUMN_IDS.IN_TRANSIT,
        title: 'In Transit',
        cards: inTransitResources.map(createResourceCard),
      },
      {
        id: COLUMN_IDS.CHECKED_IN,
        title: 'Checked In',
        cards: checkedInResources.map(createResourceCard),
      },
      {
        id: COLUMN_IDS.CHECKED_OUT,
        title: 'Checked Out',
        cards: checkedOutResources.map(createResourceCard),
      },
    ];

    return { columns };
  }, [filteredResources, selectedCheckedInStatus, selectedCheckedOutStatus]);

  // Custom card renderer
  const renderCard = (card: KanbanCard) => {
    return (
      <ResourceCard
        card={card}
        onCheckIn={handleCheckIn}
        onUpdateCheckIn={handleUpdateCheckIn}
        onStatusUpdate={handleStatusUpdate}
        onCheckOut={handleCheckOut}
        onUpdateCheckOut={handleUpdateCheckOut}
        onCancel={onResourceCancel}
        onExportICS221={handleExportICS221}
      />
    );
  };

  // Column styling
  const getColumnStyle = (columnId: string) => {
    const baseStyle = {
      borderTop: '4px solid',
    };

    switch (columnId) {
      case COLUMN_IDS.IN_TRANSIT:
        return { ...baseStyle, borderTopColor: '#17a2b8' }; // Blue
      case COLUMN_IDS.CHECKED_IN:
        return { ...baseStyle, borderTopColor: '#28a745' }; // Green
      case COLUMN_IDS.CHECKED_OUT:
        return { ...baseStyle, borderTopColor: '#6c757d' }; // Gray
      default:
        return baseStyle;
    }
  };

  // Use centralized status filter options
  const checkedInStatusOptions = CHECKED_IN_STATUS_OPTIONS;
  const checkedOutStatusOptions = CHECKED_OUT_STATUS_OPTIONS;

  const selectedCheckedInOption = checkedInStatusOptions.find(
    (opt) => opt.value === selectedCheckedInStatus
  );

  const selectedCheckedOutOption = checkedOutStatusOptions.find(
    (opt) => opt.value === selectedCheckedOutStatus
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="d-flex gap-3 mb-3">
        <div className="flex-fill">
          <label className="form-label small text-muted">Resource</label>
          <ResourceFilters
            selectedResourceType={selectedResourceType}
            onResourceTypeChange={setSelectedResourceType}
            availableResourceTypes={availableResourceTypes}
          />
        </div>
        <div className="flex-fill">
          <label className="form-label small text-muted">
            Checked In Status
          </label>
          <StylishNewSelect
            options={checkedInStatusOptions}
            value={selectedCheckedInOption}
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedCheckedInStatus(
                (option?.value || 'all') as StatusFilter
              );
            }}
            placeholder="Filter by Checked In Status"
          />
        </div>
        <div className="flex-fill">
          <label className="form-label small text-muted">
            Checked Out Status
          </label>
          <StylishNewSelect
            options={checkedOutStatusOptions}
            value={selectedCheckedOutOption}
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedCheckedOutStatus(
                (option?.value || 'all') as StatusFilter
              );
            }}
            placeholder="Filter by Checked Out Status"
          />
        </div>
      </div>

      <div className="mb-3">
        <Form.Control
          type="text"
          placeholder="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
        {debouncedSearchText && (
          <small className="text-muted d-block mt-1">
            Showing resources matching &ldquo;{debouncedSearchText}&rdquo;
          </small>
        )}
      </div>

      {/* Column Action Buttons */}
      <div
        className="d-flex justify-content-between mb-2"
        style={{ paddingLeft: '0.5rem', paddingRight: '0.5rem' }}
      >
        <div style={{ flex: 1, textAlign: 'center' }}>
          {/* In Transit Actions */}
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          {/* Checked In Actions */}
          {onExportCheckedIn && (
            <StylishNewButton
              className="button--secondary btn-sm"
              onClick={onExportCheckedIn}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              Preview ICS 211
            </StylishNewButton>
          )}
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          {/* Checked Out Actions */}
          <StylishNewButton
            className="button button--secondary btn-sm"
            onClick={handleExportICS238}
            title="Preview all incident resources as ICS 238 demobilization tracking table"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            Preview ICS 238
          </StylishNewButton>
        </div>
      </div>

      <Kanban
        board={board}
        renderCard={renderCard}
        onCardMove={handleCardMove}
        columnStyle={getColumnStyle}
        height="calc(100vh - 380px)"
        containerStyle={{ padding: '0.5rem', marginBottom: '0' }}
      />

      {showCheckInDrawer && selectedResource && (
        <CheckInDrawer
          resource={selectedResource}
          onClose={() => {
            setShowCheckInDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            // Complete the drag action by updating with check-in data
            onResourceUpdate?.({
              ...selectedResource,
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
              status_since: new Date().toISOString(),
            });
            setShowCheckInDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}

      {showUpdateCheckInDrawer && selectedResource && (
        <CheckInDrawer
          resource={selectedResource}
          isUpdate={true}
          onClose={() => {
            setShowUpdateCheckInDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            // Update check-in data without changing the check-in datetime
            const updateData = { ...data };
            delete updateData.checkin_datetime; // Remove check-in datetime to prevent update

            // Merge with selected resource but ensure checkin_datetime is not included
            const { checkin_datetime: _checkin_datetime, ...restOfResource } =
              selectedResource;
            const mergedData = {
              ...restOfResource,
              ...updateData,
            };

            onResourceUpdate?.(mergedData as ResourceInIncident);
            setShowUpdateCheckInDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}

      {showStatusDrawer && selectedResource && (
        <StatusUpdateDrawer
          resource={selectedResource}
          onClose={() => {
            setShowStatusDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            onResourceUpdate?.({ ...selectedResource, ...data });
            setShowStatusDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}

      {showCheckOutDrawer && selectedResource && (
        <CheckOutDrawer
          resource={selectedResource}
          onClose={() => {
            setShowCheckOutDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            // Complete the drag action by updating with checkout data
            onResourceUpdate?.({
              ...selectedResource,
              ...data,
              current_status: RESOURCE_STATUSES.CHECKED_OUT,
            });
            setShowCheckOutDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}

      {showUpdateCheckOutDrawer && selectedResource && (
        <CheckOutDrawer
          resource={selectedResource}
          isUpdate={true}
          onClose={() => {
            setShowUpdateCheckOutDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            // Update check-out data without changing the check-out datetime
            const updateData = { ...data };
            delete updateData.checkout_datetime; // Remove check-out datetime to prevent update

            onResourceUpdate?.({ ...selectedResource, ...updateData });
            setShowUpdateCheckOutDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}
    </>
  );
};

export default ResourceKanban;
